import { getQuote, getQuoteReport, getQuotes } from "@stryker/core/api/quotes";
import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePenIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AnalyticIcon from "@/assets/icons/analytic.svg?react";
import DownloadIcon from "@/assets/icons/download.svg?react";
import Status from "@/components/job/Status";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Typography } from "@/components/ui/typography";
import useLocationQuery from "@/hooks/useLocationQuery";
import { getMargin } from "@/utils/calculations";

export default function Quote() {
  const { id } = useParams();
  const query = useLocationQuery();
  const jobNumber = query.get("job_number");
  const repairSystemId = query.get("repair_system");
  const navigate = useNavigate();

  const { data: quote, isLoading } = useQuery({
    queryKey: [QueryKeys.QuoteDetails, id],
    queryFn: id ? () => getQuote(id) : skipToken,
  });

  const totalRevenue = quote?.total_revenue == null ? null : Number(quote.total_revenue);
  const totalCost = quote?.total_cost == null ? null : Number(quote.total_cost);
  const adjustedRevenue = quote?.total_revenue_override == null ? null : Number(quote.total_revenue_override);

  const hasTotalRevenue = totalRevenue !== null && Number.isFinite(totalRevenue);
  const hasTotalCost = totalCost !== null && Number.isFinite(totalCost);
  const hasRevenueOverride = adjustedRevenue !== null && Number.isFinite(adjustedRevenue);

  const preAdjustedMarginValue =
    hasTotalRevenue && hasTotalCost
      ? getMargin({
          totalQuotes: totalRevenue,
          totalCosts: totalCost,
        })
      : null;

  const adjustedMarginValue =
    hasRevenueOverride && hasTotalCost
      ? getMargin({
          totalQuotes: adjustedRevenue,
          totalCosts: totalCost,
        })
      : null;

  const preAdjustedMargin =
    preAdjustedMarginValue !== null && Number.isFinite(preAdjustedMarginValue) ? preAdjustedMarginValue : null;
  const adjustedMargin =
    adjustedMarginValue !== null && Number.isFinite(adjustedMarginValue) ? adjustedMarginValue : null;

  const { data: quoteListItem } = useQuery({
    queryKey: [QueryKeys.QuotesList, "download-name", jobNumber, id],
    queryFn: jobNumber ? () => getQuotes({ jobNumber, page: 1 }) : skipToken,
    select: (response) => response.results.find((item) => String(item.quote?.id) === id),
  });

  const { data: canViewCostAndMargin } = useQuery({
    ...profileQueryOptions(),
    select: profileSelectors.canViewCostAndMargin,
  });

  const queryClient = useQueryClient();
  const { data: quoteReport } = useQuery({
    queryKey: [QueryKeys.JobReport, id],
    queryFn: id && !quote?.is_draft ? () => getQuoteReport(id) : skipToken,
    refetchInterval: (response) => {
      const res = response.state.data;
      if (!res || res.result === "null" || (!res.result && !res.error)) {
        return 2000;
      }
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.QuoteDetails, id] });
      return false;
    },
  });
  const downloadReport = () => {
    if (quoteReport?.result) {
      fetch(quoteReport.result, {
        method: "GET",
        headers: {},
      })
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          const fileName = [jobNumber, quoteListItem?.plant_name]
            .filter(Boolean)
            .join("_")
            .replace(/[\\/:*?"<>|]/g, "_");
          link.setAttribute("download", `${fileName || quote?.id}_Quote.pdf`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        });
    }
  };

  const viewReport = () => {
    if (quoteReport?.result) {
      window.open(quoteReport.result as string, "_blank")?.focus();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner svgClassName="h-20 w-20" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 md:gap-5 mb-4">
        <Link to="/quotes">
          <Typography className="text-gray-500 text-sm md:text-base" weight="medium">
            Quotes
          </Typography>
        </Link>
        <Typography className="text-gray-500 text-sm md:text-base">/</Typography>
        <Typography className="text-primary-500 text-sm md:text-base" variant="text-small" weight="semibold">
          {jobNumber}
        </Typography>
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <Typography variant="display-md" weight="semibold" className="text-xl md:text-2xl lg:text-3xl break-words">
          {jobNumber}
        </Typography>
        <div className="md:ml-auto items-center flex flex-col md:flex-row gap-2 w-full md:w-auto">
          {quoteReport?.result && (
            <>
              <Button variant="secondary" onClick={viewReport} className="w-full md:w-auto text-xs md:text-sm">
                <AnalyticIcon className="me-2" />
                View as PDF file
              </Button>
              <Button className="w-full md:w-fit text-xs md:text-sm" onClick={downloadReport}>
                <DownloadIcon />
                Download
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              if (quote) {
                const url = `/quotes/create?job_number=${jobNumber}&quote_id=${quote.id}&repair_system=${repairSystemId}`;
                navigate(url);
              }
            }}
            className="w-full md:w-auto text-xs md:text-sm"
          >
            {!quote?.is_draft && <FilePenIcon className="mr-2" width={16} />}
            {quote?.is_draft ? "Finish draft" : "Edit"}
          </Button>
          <Status status={quoteReport?.status} innerContainerClassName="h-10" />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row items-start justify-start gap-4 mt-4">
        <div className="flex flex-col gap-4 w-full lg:w-auto">
          <Card className="p-4 border-success-300">
            <CardTitle>
              <Typography variant="text-xl" weight="semibold" className="text-lg md:text-xl">
                Cost Details
              </Typography>
            </CardTitle>
            <CardContent className="p-0 overflow-x-auto -mx-4 md:mx-0">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px] text-xs md:text-sm">Item</TableHead>
                    <TableHead className="text-xs md:text-sm">Quantity</TableHead>
                    <TableHead className="text-xs md:text-sm">Unit</TableHead>
                    {canViewCostAndMargin && (
                      <TableHead className="text-right text-xs md:text-sm">Cost Per Unit</TableHead>
                    )}
                    <TableHead className="text-right text-xs md:text-sm">Price Per Unit</TableHead>
                    {canViewCostAndMargin && (
                      <TableHead className="text-right text-xs md:text-sm">Total Cost</TableHead>
                    )}
                    <TableHead className="text-right text-xs md:text-sm">Total Revenue</TableHead>
                    {canViewCostAndMargin && <TableHead className="text-right text-xs md:text-sm">Margin</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote?.quote_details.map((quote) => (
                    <TableRow key={quote.item_name}>
                      <TableCell className="font-medium text-xs md:text-sm">{quote.item_name}</TableCell>
                      <TableCell className="text-center text-xs md:text-sm">{quote.quantity}</TableCell>
                      <TableCell className="text-center text-xs md:text-sm"> {quote.unit}</TableCell>
                      {canViewCostAndMargin && (
                        <TableCell className="text-center text-xs md:text-sm">${quote.cost_per_unit}</TableCell>
                      )}
                      <TableCell className="text-center text-xs md:text-sm">${quote.price_per_unit}</TableCell>
                      {canViewCostAndMargin && (
                        <TableCell className="text-center text-xs md:text-sm">${quote.total_cost}</TableCell>
                      )}
                      <TableCell className="text-center text-xs md:text-sm">${quote.total_revenue}</TableCell>
                      {canViewCostAndMargin && (
                        <TableCell className="text-center text-xs md:text-sm">{quote.margin.toFixed(2)}%</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card className="w-full border-success-300 p-4">
            <CardTitle className="flex flex-col gap-2">
              <div className="flex justify-between gap-2">
                <Typography variant="text-xl" weight="semibold" className="text-base md:text-xl">
                  Total revenue:
                </Typography>
                <Typography variant="text-lg" weight="medium" className="text-base md:text-lg">
                  {hasTotalRevenue ? `$${totalRevenue}` : "N/A"}
                </Typography>
              </div>

              {hasRevenueOverride && (
                <div className="flex justify-between gap-2">
                  <Typography variant="text-xl" weight="semibold" className="text-base md:text-xl">
                    Adjusted revenue:
                  </Typography>
                  <Typography variant="text-lg" weight="medium" className="text-base md:text-lg">
                    ${adjustedRevenue}
                  </Typography>
                </div>
              )}

              {canViewCostAndMargin && (
                <div className="flex justify-between gap-2">
                  <Typography variant="text-xl" weight="semibold" className="text-base md:text-xl">
                    Total cost:
                  </Typography>
                  <Typography variant="text-lg" weight="medium" className="text-base md:text-lg">
                    {hasTotalCost ? `$${totalCost.toFixed(0)}` : "N/A"}
                  </Typography>
                </div>
              )}
            </CardTitle>
          </Card>
        </div>

        {canViewCostAndMargin && (
          <Card className="w-full border-success-300 p-4 lg:w-auto lg:min-w-[280px]">
            <CardContent className="flex flex-col gap-2 p-0">
              <div className="flex justify-between gap-4">
                <Typography variant="text-xl" weight="semibold" className="text-base md:text-xl">
                  {hasRevenueOverride ? "Pre-adjusted margin:" : "Margin:"}
                </Typography>
                <Typography variant="text-lg" weight="medium" className="text-base md:text-lg">
                  {preAdjustedMargin === null ? "N/A" : `${preAdjustedMargin.toFixed(2)}%`}
                </Typography>
              </div>

              {hasRevenueOverride && (
                <div className="flex justify-between gap-4">
                  <Typography variant="text-xl" weight="semibold" className="text-base md:text-xl">
                    Adjusted margin:
                  </Typography>
                  <Typography variant="text-lg" weight="medium" className="text-base md:text-lg">
                    {adjustedMargin === null ? "N/A" : `${adjustedMargin.toFixed(2)}%`}
                  </Typography>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
